import { createFileRoute } from '@tanstack/react-router';
import { Badge, Button, Card, CopyButton, Group, Loader, Modal, NumberInput, Stack, Table, TagsInput, Text, TextInput, Title } from '@mantine/core';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { useDisclosure } from '@mantine/hooks';
import { IconCheck, IconCopy, IconKey, IconPlus, IconTrash } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { notifyError } from '@/shared/lib/notify';
import { listApiKeys, createApiKey, revokeApiKey, type CreatedApiKey } from '@/features/api-keys/api';
import dayjs from 'dayjs';
import { Paginated } from '@/shared/components/DataTable/TablePagination';
import { MW_CODE_STYLE } from '@/shared/mobile/tokens';

export const Route = createFileRoute('/_app/settings/api-keys')({ component: ApiKeysPage });

function ApiKeysPage() {
  const qc = useQueryClient();
  const [createOpen, { open, close }] = useDisclosure();
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ['api-keys'], queryFn: listApiKeys });
  const create = useMutation({
    mutationFn: ({ name, scopes, days }: { name: string; scopes: string[]; days: number | null }) =>
      createApiKey(name, scopes, days),
    onSuccess: (k) => {
      setCreatedKey(k);
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      close();
    },
    onError: (err) => notifyError(err, 'Creazione fallita'),
  });
  const revoke = useMutation({
    mutationFn: revokeApiKey,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      notifications.show({ color: 'green', message: 'Chiave revocata' });
    },
  });

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>API Keys</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={open}>Nuova chiave</Button>
      </Group>
      <Text c="dimmed" size="sm">
        Le API key permettono ad applicazioni esterne (webhook, integrazioni, script) di chiamare la piattaforma
        senza login utente. Ogni chiave ha scope granulari e può scadere.
      </Text>
      <Card withBorder padding={0} radius="lg">
        {isError ? (
        <QueryErrorAlert error={error} onRetry={() => void refetch()} />
      ) : isLoading ? (
          <Group p="lg"><Loader /></Group>
        ) : (
          <Paginated rows={(data ?? [])} label="chiavi">
          {(pageRows) => (
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nome</Table.Th>
                <Table.Th>Prefix</Table.Th>
                <Table.Th>Scopes</Table.Th>
                <Table.Th>Creata</Table.Th>
                <Table.Th>Scade</Table.Th>
                <Table.Th>Ultimo uso</Table.Th>
                <Table.Th>Stato</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pageRows.map((k) => (
                <Table.Tr key={k.id}>
                  <Table.Td>{k.name}</Table.Td>
                  <Table.Td><Text size="xs" style={MW_CODE_STYLE}>{k.prefix}…</Text></Table.Td>
                  <Table.Td>
                    {(k.scopes ?? '').split(',').filter(Boolean).map((s) => (
                      <Badge key={s} variant="light" mr={4}>{s}</Badge>
                    ))}
                  </Table.Td>
                  <Table.Td>{k.createdAt ? dayjs(k.createdAt).format('DD/MM/YYYY') : '—'}</Table.Td>
                  <Table.Td>{k.expiresAt ? dayjs(k.expiresAt).format('DD/MM/YYYY') : 'mai'}</Table.Td>
                  <Table.Td>{k.lastUsedAt ? dayjs(k.lastUsedAt).format('DD/MM/YYYY HH:mm') : 'mai'}</Table.Td>
                  <Table.Td>
                    {k.revokedAt ? <Badge color="red">Revocata</Badge> : <Badge color="green">Attiva</Badge>}
                  </Table.Td>
                  <Table.Td>
                    {!k.revokedAt && (
                      <Button loading={revoke.isPending} size="xs" variant="subtle" color="red" leftSection={<IconTrash size={14} />} onClick={() => revoke.mutate(k.id)}>
                        Revoca
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
              {!data?.length && (
                <Table.Tr>
                  <Table.Td colSpan={8}>
                    <Stack align="center" py="lg" gap={4}>
                      <IconKey size={32} opacity={0.4} />
                      <Text c="dimmed">Nessuna chiave attiva</Text>
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
          )}
          </Paginated>
        )}
      </Card>

      <CreateModal opened={createOpen} onClose={close} onCreate={(v) => create.mutate(v)} loading={create.isPending} />

      <Modal opened={Boolean(createdKey)} onClose={() => setCreatedKey(null)} title="Chiave generata" centered>
        <Stack>
          <Text size="sm" c="orange" fw={600}>Salva questa chiave adesso — non sarà più visibile.</Text>
          <Card withBorder padding="md">
            <Group justify="space-between">
              <Text size="sm" style={{ ...MW_CODE_STYLE, wordBreak: 'break-all' }}>
                {createdKey?.key}
              </Text>
              <CopyButton value={createdKey?.key ?? ''}>
                {({ copied, copy }) => (
                  <Button size="xs" variant="light" leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />} onClick={copy}>
                    {copied ? 'Copiata' : 'Copia'}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </Card>
          <Button onClick={() => setCreatedKey(null)} fullWidth>Ho salvato la chiave</Button>
        </Stack>
      </Modal>
    </Stack>
  );
}

function CreateModal({ opened, onClose, onCreate, loading }: { opened: boolean; onClose: () => void; onCreate: (v: { name: string; scopes: string[]; days: number | null }) => void; loading: boolean }) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [days, setDays] = useState<number | null>(365);
  return (
    <Modal opened={opened} onClose={onClose} title="Nuova API key" centered>
      <Stack>
        <TextInput label="Nome" required placeholder="Es. Webhook Stripe" value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <TagsInput
          label="Scopes"
          description="Es. tasks.read, projects.*, *.read, * (super-scope)"
          placeholder="Premi Invio dopo ogni scope"
          value={scopes}
          onChange={setScopes}
        />
        <NumberInput label="Scade fra (giorni)" description="Lascia vuoto per chiave permanente" value={days ?? undefined} onChange={(v) => setDays(typeof v === 'number' ? v : null)} min={1} max={3650} />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Annulla</Button>
          <Button onClick={() => onCreate({ name, scopes, days })} loading={loading} disabled={!name || !scopes.length}>
            Crea chiave
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
